import { styled } from '@uhg-abyss/web/tools/styled';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { NavMenuPrimitives } from '@uhg-abyss/web/ui/NavMenu';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { config } from '@uhg-abyss/web/tools/config';
import { useSelector } from 'react-redux';
import { StateRouter } from '@uhg-abyss/web/ui/StateRouter';
import { PageHeaderPrimitives } from '@uhg-abyss/web/ui/PageHeader';
import { checkInternalRole } from '@src/common/Utils/utils';

const TopNavMenu = ({
  userInfo,
  signOut,
  signIn,
  fireHeaderEvent,
  routeToPage,
  isMobile,
}) => {
  const Icon = styled('div', {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '14px',
    width: '14px',
    borderRadius: '50%',
    border: 'solid 1px #002677',
    marginLeft: '4px',
    fontSize: '10px',
    fontWeight: '$bold',
  });
  const TinManagementHeader = styled('div', {
    marginLeft: '15px',
    color: '#196ECF',
    fontWeight: 'bold',
  });

  const TinManagementDescription = styled('div', {
    color: '#424242',
    fontWeight: 'normal',
  });

  const dsUserInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });
  const hasInternalRole = checkInternalRole(dsUserInfo);
  const HEADER_LINK_CLICK = 'header link click';
  const [menuTitle, setMenuTitle] = React.useState(
    userInfo?.uuid !== '' ? 'Organization Management' : 'Sign in'
  );

  const renderSignOutLink = () => {
    if (userInfo?.uuid !== '') {
      return (
        <NavMenuPrimitives.Link onClick={signOut} variant="inverted">
          <Layout.Group space={0} css={{ fontSize: '15px' }}>
            Sign Out&nbsp;
            <IconSymbol icon="logout" size="$sm" variant="outlined" />
          </Layout.Group>
        </NavMenuPrimitives.Link>
      );
    }
    return null;
  };

  const renderSignInLink = () => {
    return (
      <Layout.Group space={0} css={{ marginLeft: '40px' }}>
        Sign in
        <Icon>
          <IconSymbol icon="person" size="$sm" />
        </Icon>
      </Layout.Group>
    );
  };

  const renderSignInMenu = () => {
    return (
      <NavMenuPrimitives.Columns>
        <NavMenuPrimitives.Column>
          <NavMenuPrimitives.MenuItem
            title="Sign into UnitedHealthcare API Marketplace"
            onClick={signIn}
            id="sign-in-button"
          />
          <NavMenuPrimitives.MenuItem
            title="Create a One Healthcare ID to register"
            onClick={signIn}
            description="New users that need access to UnitedHealthcare API Marketplace are required to create a OneHealthcare ID"
            id="one-healthcare-Id"
          />
        </NavMenuPrimitives.Column>
      </NavMenuPrimitives.Columns>
    );
  };

  const renderOrgMenu = () => {
    const { isAdmin } = dsUserInfo || {};
    const enableTinManagement = config('TIN_MANAGEMENT_CONFIG') && isAdmin;
    return (
      <NavMenuPrimitives.Columns>
        <NavMenuPrimitives.Column>
          {!hasInternalRole && (
            <NavMenuPrimitives.MenuItem
              title="Credentials"
              description="Manage and generate client ID, secret, and OAuth token"
              onClick={() => {
                fireHeaderEvent('Credentials', 'header', HEADER_LINK_CLICK);
                routeToPage({
                  url: '/welcome/credentials',
                });
              }}
            />
          )}

          {hasInternalRole && (
            <NavMenuPrimitives.MenuItem
              title="Create Communication"
              description="Create, save and send a communication"
              onClick={() => {
                fireHeaderEvent(
                  'Create Communication',
                  'header',
                  HEADER_LINK_CLICK
                );
                routeToPage({
                  url: '/welcome/create-communication',
                });
              }}
            />
          )}
          {enableTinManagement && (
            <React.Fragment>
              <TinManagementHeader>
                TIN Management
                <TinManagementDescription>
                  Upload and modify your tins below
                </TinManagementDescription>
              </TinManagementHeader>

              <NavMenuPrimitives.MenuItem
                title="Review and Remove TINs"
                onClick={() => {
                  fireHeaderEvent(
                    'Review and Remove TINs',
                    'header',
                    HEADER_LINK_CLICK
                  );
                  routeToPage({
                    url: '/welcome/review-tins',
                  });
                }}
              />
              <NavMenuPrimitives.MenuItem
                title="Add New TINs"
                onClick={() => {
                  fireHeaderEvent('Add New TINs', 'header', HEADER_LINK_CLICK);
                  routeToPage({
                    url: '/welcome/add-tins',
                  });
                }}
              />
            </React.Fragment>
          )}
        </NavMenuPrimitives.Column>
      </NavMenuPrimitives.Columns>
    );
  };

  const DropdownMenuContent = () => {
    if (
      (dsUserInfo?.status && dsUserInfo?.status === 'ACTIVE') ||
      hasInternalRole
    ) {
      return renderOrgMenu();
    }
    if (userInfo?.uuid === '') {
      return renderSignInMenu();
    }
    return null;
  };

  useEffect(() => {
    const title = userInfo?.uuid !== '' ? 'Organization Management' : 'Sign in';
    setMenuTitle(title);
  }, [userInfo?.uuid]);

  return isMobile ? (
    <React.Fragment>
      <StateRouter.Provider initialPath="/home/org">
        <NavMenuPrimitives.RouterHeader />
        <StateRouter.Routes>
          <StateRouter.Route path="/home/org" title="Back">
            <Layout.Stack
              grow
              as="ul"
              aria-labelledby="utility-items-title"
              css={{
                '&[li]': {
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                },
              }}
            >
              <li>
                <PageHeaderPrimitives.DrawerLink
                  title={`${menuTitle}`}
                  path="/home/org/test"
                />
              </li>
            </Layout.Stack>
          </StateRouter.Route>
          {/* code for sign in link mobile */}
          {userInfo?.uuid === '' && (
            <StateRouter.Route path="/home/org/test" title={`${menuTitle}`}>
              {renderSignInLink()}
              {DropdownMenuContent()}
            </StateRouter.Route>
          )}

          {/* code for org menu mobile */}
          {((dsUserInfo?.status && dsUserInfo?.status === 'ACTIVE') ||
            hasInternalRole) && (
            <StateRouter.Route path="/home/org/test" title={`${menuTitle}`}>
              {DropdownMenuContent()}
            </StateRouter.Route>
          )}
        </StateRouter.Routes>
      </StateRouter.Provider>

      {userInfo?.uuid !== '' && (
        <Layout.Stack
          grow
          as="ul"
          aria-labelledby="utility-items-title"
          css={{
            '&[li]': {
              listStyle: 'none',
              margin: 0,
              padding: 0,
            },
          }}
        >
          <li>
            <NavMenuPrimitives.MenuItem onClick={signOut} variant="inverted">
              <Layout.Group
                space={0}
                css={{ fontWeight: 700, color: '#002677' }}
              >
                Sign Out&nbsp;
                <IconSymbol icon="logout" size="$sm" variant="outlined" />
              </Layout.Group>
            </NavMenuPrimitives.MenuItem>
          </li>
        </Layout.Stack>
      )}
    </React.Fragment>
  ) : (
    <NavMenuPrimitives.Provider>
      <NavMenuPrimitives.Root
        zIndex={101}
        enableOutsideScroll={false}
        variant="inverted"
        css={{
          '.abyss-nav-menu-scroll-button-right': {
            display: 'none !important',
          },
          'abyss-nav-menu-scroll-button-right': {
            display: 'none !important',
          },
        }}
      >
        <NavMenuPrimitives.List>
          <NavMenuPrimitives.Item>
            <NavMenuPrimitives.Content
              style={{
                display: 'block' /* Or flex, depending on your layout */,
                width: 'auto' /* Allow the width to adjust based on content */,
                maxWidth:
                  '100%' /* Prevent it from exceeding the parent container */,
              }}
            >
              {/* org menu starts here */}
              {((dsUserInfo?.status && dsUserInfo?.status === 'ACTIVE') ||
                hasInternalRole) &&
                renderOrgMenu()}
              {/* org menu ends here */}
              {userInfo?.uuid === '' && renderSignInMenu()}
            </NavMenuPrimitives.Content>
            {/* org trigger */}
            {((dsUserInfo?.status && dsUserInfo?.status === 'ACTIVE') ||
              hasInternalRole) && (
              <NavMenuPrimitives.Trigger
                variant="inverted"
                css={{ fontSize: '15px' }}
              >
                Organization Management
              </NavMenuPrimitives.Trigger>
            )}
            {userInfo?.uuid === '' && (
              <NavMenuPrimitives.Trigger
                variant="inverted"
                css={{ fontSize: '15px' }}
              >
                Sign in
              </NavMenuPrimitives.Trigger>
            )}
            {/* org trigger */}
          </NavMenuPrimitives.Item>
          {renderSignOutLink()}
        </NavMenuPrimitives.List>
        <NavMenuPrimitives.Viewport
          style={{ minWidth: '500px', top: '0px', right: '175px' }}
        />
      </NavMenuPrimitives.Root>
    </NavMenuPrimitives.Provider>
  );
};

TopNavMenu.propTypes = {
  userInfo: PropTypes.shape({
    uuid: PropTypes.string,
  }),
  signOut: PropTypes.func,
  signIn: PropTypes.func,
  fireHeaderEvent: PropTypes.func,
  routeToPage: PropTypes.func,
  isMobile: PropTypes.bool,
};

TopNavMenu.defaultProps = {
  userInfo: {
    uuid: '',
  },
  signOut: () => {},
  signIn: () => {},
  fireHeaderEvent: () => {},
  routeToPage: () => {},
  isMobile: false,
};
export { TopNavMenu };
